import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { useBreadcrumb } from 'shared/context/BreadcrumbContext';
import axiosInstance from 'shared/services/api/axiosInstance';
import { formatRelativeTime } from 'shared/utils/dateUtils';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import EventDetailView from 'features/activity/components/EventDetailView';
import TableFooter from 'shared/components/tables/TableFooter';
import { primary, primaryAlpha } from 'shared/theme/trodoColors';
import { openUserProfile } from 'features/users/openUserProfile';

const PAGE_BG = 'var(--surface-sunken)';
const BORDER = 'var(--border-default)';

// ─── helpers ───────────────────────────────────────────────────────────────
const formatDateHeader = (iso) => {
  if (!iso) return 'N/A';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return 'N/A'; }
};
const formatTimeOnly = (iso) => {
  if (!iso) return 'N/A';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch { return 'N/A'; }
};
const toDateKey = (iso) => {
  if (!iso) return 'no-date';
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return 'no-date'; }
};

/** Normalize a group timeline row. Preserves the raw eventId (numeric from Postgres) for API calls. */
function normalizeGroupTimelineEvent(row, fallbackIndex) {
  if (!row || typeof row !== 'object') {
    return { eventId: null, rowKey: `row-${fallbackIndex}`, eventName: '', timestamp: null, distinctId: '' };
  }
  const eventId = row.eventId ?? row.id ?? row.event_id ?? null;
  // rowKey is always a unique non-null string used for React keys and expansion state
  const rowKey = eventId != null ? String(eventId) : `row-${fallbackIndex}`;
  return {
    eventId,          // raw numeric id (may be null if backend doesn't send it)
    rowKey,           // always a unique string
    eventName: row.eventName ?? row.event_name ?? '',
    timestamp: row.timestamp ?? null,
    distinctId: row.distinctId ?? row.distinct_id ?? ''
  };
}

/**
 * Full-page group detail view at /:team/activity/groups/:groupKey/:groupId
 *
 * Layout:
 *   Left panel  — group profile card + members list
 *   Right panel — event timeline for the group's members
 */
const GroupDetailPage = () => {
  const params = useParams();
  const { team, groupKey, groupId } = params;

  const teamId = useMemo(() => {
    try {
      const d = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      const fromLs = localStorage.getItem('selectedTeam');
      return (d.id ?? d._id ?? fromLs) ?? '';
    } catch (_) {
      return localStorage.getItem('selectedTeam') ?? '';
    }
  }, []);

  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [groupError, setGroupError] = useState(null);

  const [events, setEvents] = useState([]);
  const [eventPage, setEventPage] = useState(1);
  const [eventTotal, setEventTotal] = useState(0);
  const [eventTotalPages, setEventTotalPages] = useState(1);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingMoreEvents, setLoadingMoreEvents] = useState(false);
  // expandedRowKey: the rowKey of the currently open row (always a non-null unique string)
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  // eventDetailById: cache keyed by numeric eventId string → full event object from API
  const [eventDetailById, setEventDetailById] = useState({});
  const [loadingEventKey, setLoadingEventKey] = useState(null);
  const eventDetailCacheRef = useRef({});
  useEffect(() => {
    eventDetailCacheRef.current = eventDetailById;
  }, [eventDetailById]);

  const styles = {
    textPrimary: 'var(--text-primary)',
    textSecondary: 'var(--text-secondary)',
    borderColor: BORDER,
    primaryBg: 'var(--surface-base)',
    pageBg: PAGE_BG,
    warmMuted: 'var(--text-muted)',
  };

  // Fetch group detail
  useEffect(() => {
    if (!teamId || !groupKey || !groupId) return;
    setLoadingGroup(true);
    setGroupError(null);
    axiosInstance
      .get(`/group-identities/${encodeURIComponent(groupKey)}/${encodeURIComponent(groupId)}`, {
        params: { team_id: teamId }
      })
      .then(r => {
        if (r.data.success) {
          setGroup(r.data.group);
          setMembers(r.data.members || []);
        } else {
          setGroupError('Group not found');
        }
      })
      .catch(err => setGroupError(err.response?.data?.error || 'Failed to load group'))
      .finally(() => setLoadingGroup(false));
  }, [teamId, groupKey, groupId]);

  // Fetch events for this group
  const fetchEvents = (page = 1, isReset = true) => {
    if (!teamId || !groupKey || !groupId) return;
    if (isReset) setLoadingEvents(true);
    else setLoadingMoreEvents(true);

    axiosInstance
      .get(`/group-identities/${encodeURIComponent(groupKey)}/${encodeURIComponent(groupId)}/events`, {
        params: { team_id: teamId, page, limit: 25 }
      })
      .then(r => {
        if (r.data.success) {
          const data = (r.data.data || []).map((row, i) => normalizeGroupTimelineEvent(row, i));
          const pag = r.data.pagination || {};
          if (isReset) setEvents(data);
          else setEvents(prev => [...prev, ...data]);
          setEventPage(pag.page || page);
          setEventTotal(pag.total || 0);
          setEventTotalPages(pag.totalPages || 1);
        }
      })
      .catch(err => console.error('Error fetching group events:', err))
      .finally(() => {
        if (isReset) setLoadingEvents(false);
        else setLoadingMoreEvents(false);
      });
  };

  useEffect(() => { fetchEvents(1, true); }, [teamId, groupKey, groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setExpandedRowKey(null);
    setEventDetailById({});
    setLoadingEventKey(null);
  }, [teamId, groupKey, groupId]);

  // When a row is expanded AND it has a numeric eventId not yet cached, fetch details
  // We look up the eventId from the events list via expandedRowKey
  useEffect(() => {
    if (!expandedRowKey) return;

    // Find the event in the list by its rowKey
    const ev = events.find(e => e.rowKey === expandedRowKey);
    if (!ev || ev.eventId == null) return; // no id → can't fetch, just show nothing
    const eidStr = String(ev.eventId);
    if (eventDetailCacheRef.current[eidStr]) return; // already cached

    // Use selectedTeam (team name) — same as EventRow — so backend resolveTeamId works
    const resolvedTeamId = localStorage.getItem('selectedTeam') || teamId;
    if (!resolvedTeamId) return;

    let cancelled = false;
    setLoadingEventKey(eidStr);
    axiosInstance
      .get(`/events/${eidStr}`, { params: { team_id: resolvedTeamId } })
      .then((r) => {
        if (cancelled || !r.data?.success || !r.data?.event) return;
        setEventDetailById((prev) => ({ ...prev, [eidStr]: r.data.event }));
      })
      .catch((err) => {
        if (!cancelled) console.error('Error loading event details:', err);
      })
      .finally(() => {
        if (!cancelled) setLoadingEventKey(null);
      });

    return () => { cancelled = true; };
  }, [expandedRowKey, events, teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMoreEvents = () => {
    if (!loadingMoreEvents && eventPage < eventTotalPages) {
      fetchEvents(eventPage + 1, false);
    }
  };

  const groupKeyLabel = groupKey
    ? groupKey.replace(/_id$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : 'Group';

  const truncateId = (id) => {
    if (!id || id.length <= 20) return id;
    return `${id.slice(0, 10)}...${id.slice(-8)}`;
  };

  // Navigate back to Activity → Users with the same group tab selected
  // (groups live under the Users activity page as dynamic tabs).
  const backPath = team
    ? `/${encodeURIComponent(team)}/activity/users?groupKey=${encodeURIComponent(groupKey || '')}`
    : null;

  useBreadcrumb([
    { label: groupKeyLabel, to: backPath },
    { label: group?.groupId || decodeURIComponent(groupId || '') },
  ]);

  return (
    <div
      className="flex flex-col min-h-screen flex-1"
      style={{ fontFamily: 'Inter, sans-serif', backgroundColor: styles.pageBg }}
    >
      {/* Main layout */}
      <div className="flex flex-1 overflow-hidden min-h-0 flex-col md:flex-row">
        {/* Left panel */}
        <div
          className="w-full md:w-[420px] flex-shrink-0 flex flex-col overflow-hidden border-b md:border-b-0"
          style={{ borderRight: `1px solid ${styles.borderColor}`, backgroundColor: styles.pageBg }}
        >
          <div className="flex-1 overflow-y-auto p-3 sm:p-7 space-y-4 sm:space-y-7">
            {loadingGroup ? (
              <div className="flex flex-col items-center justify-center py-12">
                <TrodoLoader size={80} label="Loading group" />
              </div>
            ) : groupError ? (
              <div className="text-center py-8 text-sm font-medium" style={{ color: 'var(--feedback-error)' }}>{groupError}</div>
            ) : group ? (
              <>
                {/* Group profile card */}
                <div
                  className="rounded-xl p-6 space-y-4 shadow-sm"
                  style={{ border: `1px solid ${styles.borderColor}`, backgroundColor: styles.primaryBg }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wider mb-1.5" style={{ color: styles.warmMuted }}>
                        {groupKeyLabel}
                      </div>
                      <div className="text-lg font-medium break-all leading-snug" style={{ color: styles.textPrimary }}>
                        {group.groupId}
                      </div>
                    </div>
                    <span
                      className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                      style={{ backgroundColor: primaryAlpha(0.1), color: primary }}
                    >
                      {group.memberCount} member{group.memberCount !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="text-sm space-y-1.5 leading-relaxed">
                    {group.updatedAt && (
                      <div>
                        <span className="font-medium" style={{ color: styles.textPrimary }}>Updated:</span>{' '}
                        <span style={{ color: styles.textSecondary }}>{formatRelativeTime(new Date(group.updatedAt))}</span>
                      </div>
                    )}
                    {group.createdAt && (
                      <div>
                        <span className="font-medium" style={{ color: styles.textPrimary }}>Created:</span>{' '}
                        <span style={{ color: styles.textSecondary }}>{formatRelativeTime(new Date(group.createdAt))}</span>
                      </div>
                    )}
                  </div>

                  {/* Custom properties */}
                  {group.customProperties && Object.keys(group.customProperties).length > 0 && (
                    <div className="pt-3" style={{ borderTop: `1px solid ${styles.borderColor}` }}>
                      <div className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: styles.textPrimary }}>
                        Properties
                      </div>
                      <div className="space-y-2.5">
                        {Object.entries(group.customProperties).map(([k, v]) => (
                          <div key={k} className="flex items-start gap-3 text-sm leading-relaxed">
                            <span className="font-medium flex-shrink-0 min-w-[5rem]" style={{ color: styles.textPrimary }}>
                              {k}:
                            </span>
                            <span className="break-all font-normal" style={{ color: styles.textSecondary }}>
                              {Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Members */}
                <div>
                  <div className="text-sm font-semibold mb-3" style={{ color: styles.textPrimary }}>
                    Members ({members.length}{group.memberCount > members.length ? ` of ${group.memberCount}` : ''})
                  </div>
                  {members.length === 0 ? (
                    <div className="text-sm" style={{ color: styles.textSecondary }}>No members yet</div>
                  ) : (
                    <div className="space-y-3">
                      {members.map((m, i) => (
                        <button
                          key={`${m.distinctId}-${i}`}
                          onClick={() => openUserProfile(m.distinctId)}
                          className="w-full flex items-center gap-3 p-4 rounded-xl text-left transition-colors hover:bg-[color:var(--surface-sunken)]"
                          style={{ border: `1px solid ${styles.borderColor}`, backgroundColor: styles.primaryBg }}
                        >
                          <div
                            className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
                            style={{ backgroundColor: primaryAlpha(0.15), color: primary }}
                          >
                            {(m.name || m.distinctId || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            {m.name && (
                              <div className="text-sm font-medium truncate" style={{ color: styles.textPrimary }}>
                                {m.name}
                              </div>
                            )}
                            <div className="text-sm truncate mt-0.5" style={{ color: styles.textSecondary }}>
                              {m.distinctId && m.distinctId.length > 20
                                ? `${m.distinctId.slice(0, 10)}...${m.distinctId.slice(-8)}`
                                : m.distinctId}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 ml-auto flex-shrink-0" style={{ color: styles.textSecondary }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Right panel: event timeline */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-7 py-5 flex-shrink-0" style={{ borderBottom: `1px solid ${styles.borderColor}`, backgroundColor: styles.primaryBg }}>
            <div className="text-xs font-medium uppercase tracking-wider" style={{ color: styles.textPrimary }}>
              Event timeline
            </div>
            {!loadingEvents && (
              <p className="text-sm mt-1.5 font-normal leading-relaxed" style={{ color: styles.textSecondary }}>
                {eventTotal.toLocaleString()} events by group members
              </p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-7 py-6" style={{ backgroundColor: styles.pageBg }}>
            {loadingEvents ? (
              <div className="flex flex-col items-center justify-center py-12">
                <TrodoLoader size={80} label="Loading events" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12 text-sm font-normal" style={{ color: styles.textSecondary }}>
                No events recorded by this group's members yet.
              </div>
            ) : (
              <>
                {/* Group events by calendar date, newest date first */}
                {(() => {
                  const grouped = {};
                  events.forEach((ev, idx) => {
                    const dk = toDateKey(ev.timestamp);
                    if (!grouped[dk]) grouped[dk] = [];
                    grouped[dk].push({ ev, idx });
                  });
                  const sortedDates = Object.keys(grouped).sort((a, b) =>
                    a === 'no-date' ? 1 : b === 'no-date' ? -1 : b.localeCompare(a)
                  );

                  return sortedDates.map((dk) => {
                    const entries = grouped[dk];
                    const dateLabel = dk === 'no-date' ? 'Unknown date' : formatDateHeader(entries[0].ev.timestamp);
                    return (
                      <div key={dk} className="mb-8">
                        {/* Date header: same rhythm as Properties */}
                        <div
                          className="text-xs font-medium uppercase tracking-wider mb-3 pb-2.5"
                          style={{ color: styles.textPrimary, borderBottom: `1px solid ${styles.borderColor}` }}
                        >
                          {String(dateLabel).toUpperCase()}
                        </div>

                        <div className="space-y-2.5">
                          {entries.map(({ ev }) => {
                          const rk = ev.rowKey;
                          const eidStr = ev.eventId != null ? String(ev.eventId) : null;
                          const isOpen = expandedRowKey === rk;
                          const displayTime = formatTimeOnly(ev.timestamp);
                          const idLabel =
                            ev.distinctId && ev.distinctId.length > 16
                              ? `${ev.distinctId.slice(0, 8)}…${ev.distinctId.slice(-6)}`
                              : ev.distinctId || '—';
                          const cachedDetail = eidStr ? eventDetailById[eidStr] : null;
                          const isLoadingThis = eidStr && loadingEventKey === eidStr;

                          return (
                            <div key={rk} className="min-w-0">
                              <button
                                type="button"
                                onClick={() => setExpandedRowKey((prev) => (prev === rk ? null : rk))}
                                className={`w-full text-left rounded-lg py-2.5 px-2 -mx-2 flex items-start justify-between gap-3 transition-colors ${
                                  isOpen ? 'bg-[color:var(--surface-sunken)]' : 'hover:bg-[color:var(--surface-sunken)]'
                                }`}
                              >
                                <div className="min-w-0 flex items-start gap-3 text-sm leading-relaxed flex-1">
                                  <span
                                    className="font-medium flex-shrink-0 min-w-[5rem] max-w-[45%] break-words"
                                    style={{ color: styles.textPrimary }}
                                  >
                                    {(ev.eventName || '—')}:
                                  </span>
                                  <span className="flex-1 min-w-0 break-all font-normal text-left" style={{ color: styles.textSecondary }}>
                                    <span className="tabular-nums whitespace-nowrap">{displayTime}</span>
                                    {ev.distinctId && (
                                      <>
                                        <span> · </span>
                                        <span
                                          role="link"
                                          tabIndex={0}
                                          className="hover:underline cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openUserProfile(ev.distinctId);
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              openUserProfile(ev.distinctId);
                                            }
                                          }}
                                        >
                                          {idLabel}
                                        </span>
                                      </>
                                    )}
                                  </span>
                                </div>
                                {isOpen
                                  ? <ChevronUp className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: styles.textSecondary }} />
                                  : <ChevronDown className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: styles.textSecondary }} />
                                }
                              </button>

                              {isOpen && (
                                <div className="pb-4 pt-1 pl-2">
                                    {isLoadingThis && !cachedDetail ? (
                                      <div className="py-4 text-sm font-normal" style={{ color: styles.textSecondary, fontFamily: 'Inter, sans-serif' }}>
                                        Loading event properties…
                                      </div>
                                    ) : cachedDetail ? (
                                      <EventDetailView event={cachedDetail} />
                                    ) : !eidStr ? (
                                      <div className="py-4 text-sm font-normal" style={{ color: styles.textSecondary, fontFamily: 'Inter, sans-serif' }}>
                                        Event details unavailable: backend may need a restart.
                                      </div>
                                    ) : (
                                      <div className="py-4 text-sm font-normal" style={{ color: styles.textSecondary, fontFamily: 'Inter, sans-serif' }}>
                                        Loading event properties…
                                      </div>
                                    )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    );
                  });
                })()}

                <TableFooter
                  shown={events.length}
                  total={eventTotal}
                  hasMore={eventPage < eventTotalPages}
                  loading={loadingMoreEvents}
                  onLoadMore={handleLoadMoreEvents}
                  className="mt-4"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupDetailPage;
